import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, FormLabel } from '@material-ui/core';
import { Check } from '@material-ui/icons';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import SelectInput from 'components/FormSelect/SelectInput';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { toast } from 'react-toastify';
import makeStyles from '@material-ui/styles/makeStyles';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';

import { post } from '../../../helpers/apiHelpers';
import Button from '../../../components/CustomButtons/Button';

const STANDARD = 'STANDARD';
const TAG = 'TAG';

const termsSupportTypes = [
  {
    value: STANDARD,
    translationKey: 'brands.newBrandForm.salesMango.mode.dedicatedFieldValue',
  },
  {
    value: TAG,
    translationKey: 'brands.newBrandForm.salesMango.mode.tags',
  },
];

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  textInput: {
    display: 'flex',
    justifyContent: 'center',
  },
});
const SalesManagoModule = ({ handleModuleUpdate, module }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isSyncing, setIsSyncing] = useState(false);

  const handleClientsSync = async () => {
    setIsSyncing(true);
    try {
      await post('/sales-manago/client-sync');
      toast.success(
        t('brands.newBrandForm.thulium.syncContacts.successMessage')
      );
      // eslint-disable-next-line no-empty -- FIXME
    } catch {
    } finally {
      setIsSyncing(false);
    }
  };

  if (!module) {
    return (
      <div>
        {t('brands.newBrandForm.moduleNotLoaded', {
          defaultValue: '{{moduleName}} not loaded.',
          replace: 'PayUModule',
        })}
      </div>
    );
  }

  // eslint-disable-next-line no-negated-condition -- FIXME
  return !module ? (
    <div>
      {t('brands.newBrandForm.moduleNotLoaded', {
        defaultValue: '{{moduleName}} not loaded.',
        // eslint-disable-next-line no-undef -- FIXME
        replace: { moduleName: __filename.split('.')[0] },
      })}
    </div>
  ) : (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={module.enabled}
                onChange={e =>
                  handleModuleUpdate({
                    ...module,
                    enabled: e.target.checked,
                  })
                }
                checkedIcon={<Check className={classes.checkedIcon} />}
                icon={<Check className={classes.uncheckedIcon} />}
                classes={{
                  checked: classes.checked,
                  root: classes.checkRoot,
                }}
              />
            }
            label={t('brands.newBrandForm.useSalesMangoLabel')}
          />

          <Button disabled={isSyncing} onClick={handleClientsSync}>
            {t('brands.newBrandForm.thulium.syncContacts')}
          </Button>
          <InfoIconTooltip
            title={t('brands.newBrandForm.thulium.syncContacts.tooltip')}
          />
        </GridItem>
        <GridItem xs={12} md={12}>
          <FormTextInput
            label={t('brands.newBrandForm.salesMango.endpoint')}
            classes={classes}
            name="salesManagoEndpoint"
            value={module.endpoint}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                endpoint: e.target.value,
              })
            }
            inputSize={12}
          />
          <FormTextInput
            label={t('brands.newBrandForm.salesMango.clientId')}
            classes={classes}
            name="salesManagoClientId"
            value={module.clientId}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                clientId: e.target.value,
              })
            }
            inputSize={12}
          />
          <FormTextInput
            label={t('brands.newBrandForm.salesMango.apiSecret')}
            classes={classes}
            name="salesManagoApiSecret"
            value={module.apiSecret}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                apiSecret: e.target.value,
              })
            }
            inputSize={12}
          />
          <FormTextInput
            label="MicroSite Key"
            classes={classes}
            name="salesManagoApiKey"
            value={module.apiKey}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                apiKey: e.target.value,
              })
            }
            inputSize={12}
          />
        </GridItem>
        <GridItem xs={12} md={12}>
          <FormTextInput
            label={t('brands.newBrandForm.salesMango.email')}
            classes={classes}
            name="salesManagoEmail"
            value={module.email}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                email: e.target.value,
              })
            }
            inputSize={12}
          />

          <FormTextInput
            label={
              <div className={classes.textInput}>
                <span>{t('brands.newBrandForm.salesMango.location')}</span>
                <InfoIconTooltip
                  title={t('brands.newBrandForm.salesMango.locationTooltip')}
                />
              </div>
            }
            classes={classes}
            name="salesManagoName"
            value={module.name}
            maxLength={64}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                name: e.target.value,
              })
            }
            inputSize={12}
          />
          <div className={classes.textInput}>
            <FormLabel className={classes.labelHorizontal}>
              {t('brands.newBrandForm.salesMango.methodOfSendingContext')}
            </FormLabel>
            <InfoIconTooltip
              title={t(
                'brands.newBrandForm.salesMango.methodOfSendingContext.tooltip',
                {
                  standard: t(
                    'brands.newBrandForm.salesMango.mode.dedicatedFieldValue'
                  ),
                  tag: t('brands.newBrandForm.salesMango.mode.tags'),
                }
              )}
            />
          </div>
          <SelectInput
            noGrid={true}
            classes={classes}
            mapBy="label"
            trackBy="value"
            name="salesManagoTermsSupportType"
            value={module.termsSupportType}
            options={termsSupportTypes.map(({ value, translationKey }) => ({
              value,
              label: t(translationKey),
            }))}
            handleChange={e =>
              handleModuleUpdate({
                ...module,
                termsSupportType: e.target.value,
              })
            }
            size={12}
          />
          <GridItem sm={12}>
            {module.termsSupportType === TAG && (
              <FormTextInput
                label={
                  <div className={classes.textInput}>
                    {t('brands.newBrandForm.salesMango.tagForConsents')}
                    <InfoIconTooltip
                      title={t(
                        'brands.newBrandForm.salesMango.tagForConsents.tooltip'
                      )}
                    />
                  </div>
                }
                classes={classes}
                name="salesManagoTermsTag"
                value={module.termsTag}
                maxLength={64}
                handleChange={e =>
                  handleModuleUpdate({
                    ...module,
                    termsTag: e.target.value,
                  })
                }
                inputSize={12}
              />
            )}

            {module.termsSupportType === STANDARD && (
              <>
                <FormTextInput
                  label={t(
                    'brands.newBrandForm.salesMango.consentDescriptionIdForEmail'
                  )}
                  classes={classes}
                  name="consentDescriptionIdForEmail"
                  value={module.consentDescriptionIdForEmail}
                  maxLength={64}
                  handleChange={e =>
                    handleModuleUpdate({
                      ...module,
                      consentDescriptionIdForEmail: e.target.value,
                    })
                  }
                  inputSize={12}
                />
                <FormTextInput
                  label={t(
                    'brands.newBrandForm.salesMango.consentDescriptionIdForSms'
                  )}
                  classes={classes}
                  name="consentDescriptionIdForSms"
                  value={module.consentDescriptionIdForSms}
                  maxLength={64}
                  handleChange={e =>
                    handleModuleUpdate({
                      ...module,
                      consentDescriptionIdForSms: e.target.value,
                    })
                  }
                  inputSize={12}
                />
                <FormTextInput
                  label={t(
                    'brands.newBrandForm.salesMango.consentDescriptionIdForPhone'
                  )}
                  classes={classes}
                  name="consentDescriptionIdForPhone"
                  value={module.consentDescriptionIdForPhone}
                  maxLength={64}
                  handleChange={e =>
                    handleModuleUpdate({
                      ...module,
                      consentDescriptionIdForPhone: e.target.value,
                    })
                  }
                  inputSize={12}
                />
              </>
            )}

            <FormLabel className={classes.labelHorizontal}>
              {t('brands.newBrandForm.salesMango.describe')}
            </FormLabel>
            <SelectInput
              noGrid={true}
              classes={classes}
              mapBy="label"
              trackBy="value"
              name="salesManagoOrderEventType"
              value={module.orderEventType}
              options={[
                {
                  label: t(
                    'brands.newBrandForm.salesMango.orderedExternalEvents'
                  ),
                  value: 'EXTERNAL_EVENT',
                },
                {
                  label: t('brands.newBrandForm.salesMango.orderedFromFeed'),
                  value: 'PRODUCT_FEED',
                },
              ]}
              handleChange={e =>
                handleModuleUpdate({
                  ...module,
                  orderEventType: e.target.value,
                })
              }
              size={6}
            />
          </GridItem>
        </GridItem>
      </GridContainer>
    </>
  );
};

export default SalesManagoModule;
